import { useEffect, useMemo } from 'react';
import { useNetworkUIStore } from '../store/networkUIStore';
import { useHistoricalPlaybackStore } from '../store/historicalPlaybackStore';
import { parseUtcTimestamp } from '../utils/timeUtils';
import { AnyNetworkElement } from '../types/network';
import { TrafostationDetails } from './sidebar/details/TrafostationDetails';
import { AbgangsleitungDetails } from './sidebar/details/AbgangsleitungDetails';
import { KabelverteilschrankDetails } from './sidebar/details/KabelverteilschrankDetails';
import { HausanschlussDetails } from './sidebar/details/HausanschlussDetails';
import { MuffeDetails } from './sidebar/details/MuffeDetails';
import { useViewMode } from '../context/useViewMode';
import { ScenarioEditSidebar } from './scenario/ScenarioEditSidebar';
import { ScenarioTrafoSidebar } from './scenario/ScenarioTrafoSidebar';
import { ScenarioKvsSidebar } from './scenario/ScenarioKvsSidebar';
import { CoLocatedElementsSection } from './sidebar/CoLocatedElementsSection';

const ElementDetails = ({ element, onClose }: { element: AnyNetworkElement; onClose: () => void }) => {
  const viewMode = useViewMode();

  if (!element) return null;

  if (viewMode === 'stress-test') {
    if (element.type === 'hausanschluss') {
      return <ScenarioEditSidebar elementId={element.id} onClose={onClose} />;
    }
    if (element.type === 'trafostation') {
      return <ScenarioTrafoSidebar elementId={element.id} onClose={onClose} />;
    }
    if (element.type === 'kabelverteilschrank') {
      return <ScenarioKvsSidebar elementId={element.id} onClose={onClose} />;
    }
  }

  switch (element.type) {
    case 'trafostation':
      return <TrafostationDetails element={element} />;
    case 'abgangsleitung':
      return <AbgangsleitungDetails element={element} />;
    case 'kabelverteilschrank':
      return <KabelverteilschrankDetails element={element} />;
    case 'hausanschluss':
      return <HausanschlussDetails element={element} />;
    case 'muffe':
      return <MuffeDetails element={element} />;
    default:
      return null;
  }
};

export const Sidebar = () => {
  const selectedElement = useNetworkUIStore(state => state.selectedElement);
  const setSelectedElement = useNetworkUIStore(state => state.setSelectedElement);
  const isCollapsed = useNetworkUIStore(state => state.isRightSidebarCollapsed);
  const toggleSidebar = useNetworkUIStore(state => state.toggleRightSidebarCollapsed);
  const setCollapsed = useNetworkUIStore(state => state.setRightSidebarCollapsed);

  // Automatically expand/collapse sidebar based on selection
  useEffect(() => {
    if (selectedElement) {
      // Element selected -> expand sidebar
      setCollapsed(false);
    } else {
      // No element selected -> collapse sidebar
      setCollapsed(true);
    }
  }, [selectedElement, setCollapsed]);

  // Playback frame in flight: the panel stays open (the element is the
  // same, only the instant changes) and shows a slim progress bar with the
  // requested instant, dimming the values until the new frame is applied —
  // otherwise a scrub looks like nothing happened until the numbers flip.
  const playbackLoading = useHistoricalPlaybackStore(state => state.mode === 'historical' && state.isLoading);
  const playbackSelectedTs = useHistoricalPlaybackStore(state => state.selectedTs);
  const playbackLabel = useMemo(() => {
    if (!playbackSelectedTs) return null;
    const d = parseUtcTimestamp(playbackSelectedTs);
    if (Number.isNaN(d.getTime())) return null;
    return `${d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}, ${d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr`;
  }, [playbackSelectedTs]);

  return (
    <div className="h-full relative pointer-events-none">
      {/* Toggle button for right Sidebar */}
      <button
        onClick={toggleSidebar}
        className={`pointer-events-auto absolute top-1/2 -translate-y-1/2 w-6 h-24 bg-gray-200/80 dark:bg-primary-700/60 backdrop-blur-md rounded-l-full shadow-sm flex items-center justify-center hover:bg-gray-300/80 dark:hover:bg-primary-600/60 transition-all duration-300 ease-in-out z-50 border-l border-t border-b border-gray-300 dark:border-primary-500 ${isCollapsed ? 'right-0 translate-x-0' : 'right-96'}`}
        title={isCollapsed ? "Sidebar einblenden" : "Sidebar ausblenden"}
      >
        <div className="w-0.5 h-10 bg-gray-400 dark:bg-primary-500 rounded-full"></div>
      </button>

      <div className={`h-full w-96 flex flex-col overflow-hidden bg-white dark:bg-primary-800 border-l border-gray-300 dark:border-primary-500 shadow-lg transition-transform duration-300 ease-in-out ${isCollapsed ? 'translate-x-full pointer-events-none' : 'translate-x-0 pointer-events-auto'}`}>
        {/* Spacer for TabBar height */}
        <div className="min-h-[40px] shrink-0" />
        {selectedElement ? (
          <div className="flex-1 overflow-y-auto relative">
            {playbackLoading && (
              <div className="sticky top-0 z-10">
                <div className="h-0.5 w-full overflow-hidden bg-orange-100 dark:bg-orange-900/40">
                  <div className="h-full w-1/3 bg-orange-500 animate-[playback-slide_1s_ease-in-out_infinite]" />
                </div>
                <div className="flex items-center gap-2 px-4 py-1 text-[11px] bg-orange-50/95 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200 border-b border-orange-200 dark:border-orange-800">
                  <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  <span>Aktualisiere auf {playbackLabel ?? 'den gewählten Zeitpunkt'} …</span>
                </div>
              </div>
            )}
            <div className={`p-4 transition-opacity duration-150 ${playbackLoading ? 'opacity-50' : 'opacity-100'}`}>
              {/* Map-click piles (trench cables, stacked markers) stay navigable */}
              <div className="mb-3 empty:hidden">
                <CoLocatedElementsSection />
              </div>
              <ElementDetails element={selectedElement} onClose={() => setSelectedElement(null)} />
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center p-8 text-center">
            <div>
              <svg className="w-10 h-10 mx-auto mb-3 text-gray-300 dark:text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
              </svg>
              <p className="text-sm font-medium text-gray-600 dark:text-primary-200">Kein Element ausgewählt</p>
              <p className="mt-1 text-xs text-gray-400 dark:text-primary-400">
                Auf der Karte ein Haus, Kabel, eine Trafostation oder einen Verteiler anklicken, um Details anzuzeigen.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
